import React from 'react';
import PropTypes from 'prop-types';
import MomentPropTypes from 'react-moment-proptypes';
import {
  Button, Col, Switch,
} from 'antd';
import {
  StopOutlined, ShareAltOutlined, FileTextOutlined, EditOutlined, NodeIndexOutlined,
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FiltersTags, RefreshComponent } from '../../../components/Table';
import { FlexLeftSpaced, FlexRightSpaced, HeaderButtonsDivider } from '../../../components';
import { DetailsTopRow } from '../../VehicleDetails/VehicleDetails.styled';
import NOP from '../../../utils/NOP';
import { getOrderType, getMonitoringOrderSnapshot, isFeatureEnabled } from '../../../store/selectors';
import { FeatureToggle } from '../../../store/actions';
import { OrderType } from '../../../types';

const OrdersTableHeader = ({
  size, lastUpdated, ordersLoading, interfaceLoading, onRefresh,
  onUpdateInterval, refreshIntervalSeconds, disableRefreshSelection, isEditable,
  onDeactivate, onShare, onEdit, onGenerateReport, onGenerateEtaReport, filters, updateFilters, clearFilters,
  showProbalyRoute, propableRouteButtonShow, renderInfo,
}) => {
  const { t } = useTranslation();
  const handleClose = (filterName) => {
    updateFilters({ [filterName]: [] });
  };
  const monitoringOrderSnapshot = useSelector(getMonitoringOrderSnapshot);
  const isEtaReportsFeatureEnabled = useSelector(isFeatureEnabled(FeatureToggle.ETA_REPORTS_ENABLED));
  const etaReportsEnabled = isEtaReportsFeatureEnabled && monitoringOrderSnapshot !== null && !isEditable;
  const type = useSelector(getOrderType);
  const shouldDisplayEtaReport = type === OrderType.TOUR.toUpperCase();

  return (
    <DetailsTopRow gutter={[10, 10]}>
      <Col xs={12}>
        <FlexLeftSpaced>
          {propableRouteButtonShow ? (
            <>
              <div style={{ marginRight: '16px' }}>
                {t('PROPERTIES.SHOW_HIDE_ROUTE')}
                <span> </span>
                <Switch
                  la
                  type="primary"
                  size={size}
                  icon={<NodeIndexOutlined />}
                  onClick={() => showProbalyRoute()}
                />
              </div>
            </>
          ) : (<></>)}
          {renderInfo()}
        </FlexLeftSpaced>
      </Col>
      <Col xs={12}>
        <FlexRightSpaced>
          <FiltersTags
            filters={{ ...filters }}
            onRemoveFilter={handleClose}
            onClearFilters={clearFilters}
          />
          {onShare && onGenerateReport && (
            <>
              <Button
                type="primary"
                size={size}
                onClick={onShare}
                icon={<ShareAltOutlined />}
              >
                {t('COMMON.SHARE')}
              </Button>
              <Button
                type="primary"
                size={size}
                icon={<FileTextOutlined />}
                onClick={onGenerateReport}
              >
                {t('VEHICLE.GENERATE_REPORT')}
              </Button>
              {shouldDisplayEtaReport && (
                <>
                  <Button
                    type="primary"
                    size={size}
                    icon={<FileTextOutlined />}
                    onClick={onGenerateEtaReport}
                    disabled={!etaReportsEnabled}
                    title={!etaReportsEnabled ? t('ORDER.SNAPSHOT.SUMMARY_NOT_AVAILABLE') : ''}
                  >
                    {t('ORDER.GENERATE_ETA_REPORT')}
                  </Button>
                </>
              )}
              <HeaderButtonsDivider />
            </>
          )}
          {onEdit && isEditable && (
            <>
              <Button
                type="primary"
                size={size}
                onClick={onEdit}
                icon={<EditOutlined />}
              >
                {t('COMMON.EDIT')}
              </Button>
              <HeaderButtonsDivider />
            </>
          )}
          {onDeactivate && isEditable && (
            <>
              <Button
                type="primary"
                size={size}
                icon={<StopOutlined />}
                onClick={onDeactivate}
              >
                {t('ORDER.DEACTIVATE')}
              </Button>
              <HeaderButtonsDivider />
            </>
          )}
          <RefreshComponent
            lastRefreshed={lastUpdated}
            size={size}
            loading={ordersLoading}
            disabled={interfaceLoading || ordersLoading}
            onRefresh={onRefresh}
            onUpdateRefreshIntervalSeconds={onUpdateInterval}
            refreshIntervalSeconds={refreshIntervalSeconds}
            disableRefreshSelection={disableRefreshSelection}
            compact
          />
        </FlexRightSpaced>
      </Col>
    </DetailsTopRow>
  );
};

OrdersTableHeader.propTypes = {
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  lastUpdated: MomentPropTypes.momentObj.isRequired,
  ordersLoading: PropTypes.bool.isRequired,
  interfaceLoading: PropTypes.bool.isRequired,
  onRefresh: PropTypes.func.isRequired,
  onUpdateInterval: PropTypes.func,
  refreshIntervalSeconds: PropTypes.number.isRequired,
  disableRefreshSelection: PropTypes.bool,
  isEditable: PropTypes.bool,
  onDeactivate: PropTypes.func,
  onShare: PropTypes.func,
  onEdit: PropTypes.func,
  onGenerateReport: PropTypes.func,
  onGenerateEtaReport: PropTypes.func,
  filters: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.any)),
  updateFilters: PropTypes.func,
  clearFilters: PropTypes.func,
  showProbalyRoute: PropTypes.func,
  propableRouteButtonShow: PropTypes.bool,
  renderInfo: PropTypes.func,
};

OrdersTableHeader.defaultProps = {
  size: 'small',
  onUpdateInterval: NOP,
  disableRefreshSelection: false,
  isEditable: false,
  onDeactivate: null,
  onShare: null,
  onEdit: null,
  onGenerateReport: null,
  onGenerateEtaReport: null,
  filters: {},
  updateFilters: NOP,
  clearFilters: NOP,
  showProbalyRoute: NOP,
  propableRouteButtonShow: false,
  renderInfo: NOP,
};

export default OrdersTableHeader;
